import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSwr from '@hooks/useSwr';
import { Spin } from 'antd';
import GoogleMapReact from 'google-map-react';
import { CSSProperties } from 'styled-components';
import CoverageMiniTable from './CoverageMiniTable';
import Marker from './Marker';
import SearchBoxGoogle from '../SearchBoxGoogle';

const GoogleMapReactWrapper = GoogleMapReact as unknown as React.FC<any>;

const CENTER_DEFAULT = {
  lat: -14.235004,
  lng: -51.92528,
};

const ZOOM_DEFAULT = 4;

interface ICoverageMap {
  allowSearch?: boolean;
  allowCoverage?: boolean;
  allowFullscreen?: boolean;
  mapHeight?: number | string;
  dataProps?: any;
  mapsZoom?: number;
  setMapsZoom?: Function;
  mapsCenter?: any;
  setMapsCenter?: Function;
  searchValue?: string | undefined;
  setSearchValue?: Function;
  clearLocation?: Function;
  setSearchParams?: Function;
  style?: CSSProperties;
}

const CoverageMap = ({
  allowSearch,
  allowCoverage,
  allowFullscreen,
  mapHeight,
  dataProps,
  mapsZoom,
  setMapsZoom,
  mapsCenter,
  setMapsCenter,
  searchValue,
  setSearchValue,
  clearLocation,
  setSearchParams,
  style,
}: ICoverageMap) => {
  const [clickCoordinates, setClickCoordinates] = useState({
    lat: undefined,
    lng: undefined,
  });

  const [clickPlot, setClickPlot] = useState();
  const [dataPlot, setDataPlot] = useState();
  const [mapsProps, setMapsProps] = useState();
  const [zoom, setZoom] = useState<number | undefined>();
  const [center, setCenter] = useState();

  const { data: clickCoverageData } = useSwr(
    clickCoordinates.lat ? '/service-proxy/coverage' : null,
    {
      params: {
        latitude: clickCoordinates.lat,
        longitude: clickCoordinates.lng,
      },
    },
  );

  const { data: coverageData } = useSwr(
    dataProps?.[0] ? '/service-proxy/coverage' : null,
    {
      params: {
        latitude: dataProps?.[0]?.lat,
        longitude: dataProps?.[0]?.lng,
      },
    },
  );

  const handleClearLocation = useCallback(() => {
    clearLocation?.();
    setClickPlot(undefined);
    setDataPlot(undefined);
    setClickCoordinates({
      lat: undefined,
      lng: undefined,
    });
  }, [clearLocation]);

  const clickModalInfo = useMemo(() => {
    return (
      <div>
        {clickCoverageData ? (
          <CoverageMiniTable data={clickCoverageData} />
        ) : (
          <div
            style={{
              height: '136px',
              width: '200px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin />
          </div>
        )}
      </div>
    );
  }, [clickCoverageData]);

  const dataModalInfoArr = useMemo(() => {
    return [
      <div key="info">
        {coverageData ? (
          <CoverageMiniTable data={coverageData} />
        ) : (
          <div
            style={{
              height: '136px',
              width: '200px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spin />
          </div>
        )}
      </div>,
    ];
  }, [coverageData]);

  const handleSetZoom = useCallback(
    (value: any) => {
      if (setMapsZoom) {
        setMapsZoom(value);
      } else {
        setZoom(value);
      }
    },
    [setMapsZoom],
  );

  const handleSetCenter = useCallback(
    (values: any) => {
      if (setMapsCenter) {
        setMapsCenter(values);
      } else {
        setCenter(values);
      }
    },
    [setMapsCenter],
  );

  useEffect(() => {
    if (dataProps && allowCoverage) {
      setDataPlot(
        // @ts-ignore
        dataProps.map(({ id, lat, lng }, index) => (
          <Marker key={id || lat} lat={lat} lng={lng} type="data">
            {dataModalInfoArr?.[index]}
          </Marker>
        )),
      );
      const { lat, lng } = dataProps[0];
      handleSetCenter({ lat, lng });
    }
  }, [allowCoverage, dataModalInfoArr, dataProps, handleSetCenter]);

  useEffect(() => {
    if (clickCoordinates.lat && clickCoordinates.lng) {
      setClickPlot(
        // @ts-ignore
        <Marker
          lat={clickCoordinates.lat}
          lng={clickCoordinates.lng}
          type="click"
        >
          {clickModalInfo}
        </Marker>,
      );
    }
  }, [clickCoordinates.lat, clickCoordinates.lng, clickModalInfo]);

  const handleMapClick = useCallback(
    ({ lat, lng }: any) => {
      const newCoordinates = {
        lat,
        lng,
      };
      handleSetCenter(newCoordinates);
      // @ts-ignore
      setClickCoordinates(newCoordinates);
    },
    [handleSetCenter],
  );

  const handleMapLoaded = useCallback(
    ({ maps, map }: any) => {
      map?.setOptions({
        draggableCursor: 'pointer',
        draggingCursor: 'grabbing',
        fullscreenControl: !!allowFullscreen,
      });

      setMapsProps(maps);
    },
    [allowFullscreen],
  );

  return (
    <div
      style={{
        height: mapHeight ?? '100%',
        width: '100%',
        borderRadius: '0 0 8px 8px',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <GoogleMapReactWrapper
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleMapLoaded}
        onClick={handleMapClick}
        bootstrapURLKeys={{
          key: process.env.REACT_APP_GOOGLE_MAPS || '',
          libraries: ['places', 'visualization'],
        }}
        defaultCenter={CENTER_DEFAULT}
        center={center ?? mapsCenter}
        defaultZoom={ZOOM_DEFAULT}
        zoom={zoom ?? mapsZoom}
        onZoomAnimationEnd={handleSetZoom}
      >
        {allowCoverage && [clickPlot, dataPlot]}
      </GoogleMapReactWrapper>

      {allowSearch && mapsProps && (
        <SearchBoxGoogle
          googleMaps={mapsProps}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          onPlacesChanged={setSearchParams}
          clearLocation={handleClearLocation}
        />
      )}
    </div>
  );
};

export default CoverageMap;
